import { Box, Typography, IconButton, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DragIndicator, Edit, Delete } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SurveyQuestion } from '../../types/survey';

const QuestionCard = styled(Card)<{ isDragging?: boolean }>(({ isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '20px',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  boxShadow: isDragging ? '0 8px 16px rgba(0, 0, 0, 0.1)' : 'none',
  backgroundColor: '#F9FAFB',
  opacity: isDragging ? 0.8 : 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#F3F4F6',
    borderColor: '#D1D5DB'
  }
}));

const DragHandle = styled(DragIndicator)({
  color: '#9CA3AF',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing'
  }
});

interface QuestionItemProps {
  question: SurveyQuestion;
  index: number;
  totalQuestions: number;
  onEdit: (question: SurveyQuestion) => void;
  onDelete: (questionId: string) => void;
}

const QuestionItem = ({ question, index, totalQuestions, onEdit, onDelete }: QuestionItemProps) => {
  const questionId = question.id || `question-${index}`;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: questionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getAnswerTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'rating': 'Rating (1-5)',
      'text': 'Text',
      'number': 'Number',
      'yesno': 'Yes/No',
      'multiple-choice': 'Multiple Choice'
    };
    return labels[type] || type;
  };

  return (
    <QuestionCard
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ 
          cursor: 'grab', 
          display: 'flex',
          alignItems: 'center',
          '&:active': { cursor: 'grabbing' },
          touchAction: 'none'
        }}
      >
        <DragHandle />
      </Box>
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: '#111827',
            lineHeight: 1.4
          }}
        >
          {index + 1}. {question.question}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.8125rem',
              color: '#6B7280',
              backgroundColor: '#E5E7EB',
              px: 1.5,
              py: 0.25,
              borderRadius: '4px',
              fontWeight: 500
            }}
          >
            {getAnswerTypeLabel(question.answerType)}
          </Typography>
          {question.isMandatory && (
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.8125rem',
                color: '#EF4444',
                backgroundColor: '#FEE2E2',
                px: 1.5,
                py: 0.25,
                borderRadius: '4px',
                fontWeight: 500
              }}
            >
              Required
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(question);
          }}
          sx={{
            color: '#6B7280',
            width: 32,
            height: 32,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#F3F4F6',
              color: '#4F46E5'
            }
          }}
        >
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(questionId);
          }}
          sx={{
            color: '#6B7280',
            width: 32,
            height: 32,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#FEE2E2',
              color: '#EF4444'
            }
          }}
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </QuestionCard>
  );
};

export default QuestionItem;

